import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const AccessDenied = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { lang } = useSelector((state) => state?.langProvider);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-100 flex items-center justify-center relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-40 h-40 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-40 w-36 h-36 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse delay-2000"></div>
        <div className="absolute bottom-40 right-40 w-28 h-28 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse delay-3000"></div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-purple-400 rounded-full animate-bounce delay-700"></div>
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-pink-400 rounded-full animate-bounce delay-1000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce delay-500"></div>
        <div className="absolute bottom-1/3 right-1/4 w-1 h-1 bg-yellow-400 rounded-full animate-bounce delay-1200"></div>
      </div>

      <div className="relative z-10 max-w-lg w-full mx-4">
        {/* Main card */}
        <div className="bg-white/80 backdrop-blur-lg shadow-2xl rounded-3xl p-10 text-center border border-white/20 relative overflow-hidden">
          {/* Card background decoration */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500"></div>

          {/* Icon with animation */}
          <div className="relative mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-red-100 to-pink-100 rounded-full mb-4 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-red-200 to-pink-200 rounded-full animate-ping opacity-30"></div>
              <i className="fas fa-calendar-times text-red-500 text-3xl relative z-10"></i>
            </div>
          </div>

          {/* Title with gradient */}
          <h1 className="text-3xl font-bold bg-clip-text text-deep-purple mb-3">
            Access Restricted
          </h1>

          {/* Subtitle */}
          <p className="text-gray-500 text-sm uppercase tracking-wider mb-6 font-medium">
            Unable to Process
          </p>

          {/* Message */}
          <div className="bg-gray-50/50 rounded-2xl mb-8 border border-gray-100">
            <p className="text-gray-700 leading-relaxed text-base font-medium">
              {state?.message ||
                "We're sorry, but you don't have permission to register for this event. This could be due to event restrictions or account limitations."}
            </p>
          </div>

          {/* Action buttons */}
          <div className="space-y-4">
            {/* Secondary button */}
            <button
              onClick={() => navigate(`/${lang}/events`)}
              className="group w-full border-2 border-gray-200 text-gray-700 px-6 py-4 rounded-2xl font-semibold hover:border-purple-300 hover:text-purple-700 hover:bg-purple-50/50 transition-all duration-300 transform hover:-translate-y-0.5"
            >
              <div className="flex items-center justify-center space-x-2">
                <i className="fas fa-calendar-alt"></i>
                <span>Explore Other Events</span>
              </div>
            </button>

            {/* Tertiary link */}
            <button
              onClick={() => navigate(state?.route || `/${lang}`)}
              className="text-gray-500 hover:text-purple-600 text-sm font-medium transition-colors duration-200 flex items-center justify-center space-x-1 pt-2"
            >
              <i className="fas fa-arrow-left text-xs"></i>
              <span>Go Back</span>
            </button>
          </div>

          {/* Help section */}
          <div className="pt-6 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-3">
              Need immediate assistance?
            </p>
            <div className="flex justify-center space-x-6 text-xs text-gray-400">
              {/* <div className="flex items-center space-x-1">
                <i className="fas fa-phone"></i>
                <span>+1 (555) 123-4567</span>
              </div> */}
              <div className="flex items-center space-x-1">
                <i className="fas fa-clock"></i>
                <span>24/7 Support</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom decorative element */}
        <div className="text-center mt-8">
          <div className="inline-flex items-center space-x-2 text-gray-400 text-sm">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-gray-300"></div>
            <i className="fas fa-shield-alt"></i>
            <span>Secure Event Platform</span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-gray-300"></div>
          </div>
        </div>
      </div>

      {/* Additional floating elements for event theme */}
      <div className="absolute top-20 right-10 text-purple-200 opacity-60">
        <i className="fas fa-music text-4xl transform rotate-12 animate-pulse"></i>
      </div>
      <div className="absolute bottom-20 left-10 text-pink-200 opacity-60">
        <i className="fas fa-ticket-alt text-3xl transform -rotate-12 animate-pulse delay-1000"></i>
      </div>
      <div className="absolute top-1/2 left-8 text-indigo-200 opacity-60">
        <i className="fas fa-calendar-check text-2xl transform rotate-45 animate-pulse delay-2000"></i>
      </div>
      <div className="absolute top-1/2 right-8 text-yellow-200 opacity-60">
        <i className="fas fa-users text-2xl transform -rotate-45 animate-pulse delay-500"></i>
      </div>
    </div>
  );
};

export default AccessDenied;
